import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { activityApi } from "../../actions";
import { brand, notice } from "../../theme/businessFlowsTheme";
import DateField from "../app/DateField";
import { EmptyState, ListSkeleton, Refusal, Surface, Toast, focusRing, rowSx } from "../workspaces/WorkspaceUi";
import { OUTCOME_LABELS, PERIODS, detailRows, exactTime, groupByDay, dayHeading, rangeFor, relativeTime } from "./activityFormat";

const EMPTY = { q: "", category: "", workspace: "", database: "", outcome: "", period: "30", from: "", to: "" };

// Done is quiet; a refusal is the one a person is looking for.
function OutcomeChip({ outcome }) {
  const refused = outcome === "refused";
  return (
    <Chip
      size="small"
      variant="outlined"
      label={OUTCOME_LABELS[outcome] || OUTCOME_LABELS.done}
      sx={{
        height: 22,
        fontWeight: 600,
        flexShrink: 0,
        ...(refused ? { color: notice.fix.fg, bgcolor: notice.fix.bg, borderColor: notice.fix.line } : { color: brand.muted, borderColor: brand.line }),
      }}
    />
  );
}

function EventRow({ event, first, onOpen }) {
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={{ xs: 0.75, sm: 1.5 }}
      alignItems={{ sm: "center" }}
      role="button"
      tabIndex={0}
      aria-label={`${event.sentence} ${exactTime(event.at)}`}
      onClick={() => onOpen(event)}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onOpen(event)}
      sx={{
        ...rowSx(first),
        cursor: "pointer",
        "&:hover": { bgcolor: brand.shell },
        "&:focus-visible": { outline: `2px solid ${brand.ink}`, outlineOffset: -2 },
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>{event.sentence}</Typography>
        <Typography variant="body2" color="text.secondary">
          {event.categoryLabel}
          {event.reason ? ` · ${event.reason}` : ""}
        </Typography>
      </Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
        <OutcomeChip outcome={event.outcome} />
        <Tooltip title={exactTime(event.at)}>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
            {relativeTime(event.at)}
          </Typography>
        </Tooltip>
      </Stack>
    </Stack>
  );
}

// The rest of an event, as a definition list - never the record as it is
// stored.
function DetailsDrawer({ event, onClose }) {
  const rows = event ? detailRows(event) : [];
  return (
    <Drawer anchor="right" open={Boolean(event)} onClose={onClose} PaperProps={{ className: "bfx", sx: { width: { xs: "100%", sm: 420 } } }}>
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 2 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, overflowWrap: "anywhere" }}>
              {event?.sentence}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75 }}>
              <OutcomeChip outcome={event?.outcome} />
              <Typography variant="body2" color="text.secondary">
                {relativeTime(event?.at)}
              </Typography>
            </Stack>
          </Box>
          <IconButton aria-label="Close details" onClick={onClose}>
            <CloseRoundedIcon />
          </IconButton>
        </Stack>

        <Box component="dl" sx={{ m: 0 }}>
          {rows.map((row) => (
            <Box key={row.label} sx={{ py: 1, borderTop: `1px solid ${brand.hairline}` }}>
              <Typography component="dt" variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: "0.02em" }}>
                {row.label}
              </Typography>
              <Typography component="dd" sx={{ m: 0, overflowWrap: "anywhere" }}>
                {row.value}
              </Typography>
            </Box>
          ))}
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          This is a record of something that happened. It can't be changed or removed.
        </Typography>
      </Box>
    </Drawer>
  );
}

// Who changed what, and when.
//
// scope "platform": everything, for bpExperts. scope "workspace": one
// workspace, for its own admins - the server decides that from the address,
// so nothing here can widen it.
export default function ActivityFeed({ scope = "platform", workspaceId = null }) {
  const [filters, setFilters] = useState(EMPTY);
  const [term, setTerm] = useState("");
  const [options, setOptions] = useState({ categories: [], outcomes: [], workspaces: [], databases: [] });
  const [page, setPage] = useState(null);
  const [events, setEvents] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  // `notice` is the theme's palette, so the toast's own text is `toast`.
  const [toast, setToast] = useState("");
  const [chosen, setChosen] = useState(null);
  // Only the newest read may change the list: a slow one that started first
  // must never land on top of it.
  const latest = useRef(0);

  const scoped = scope === "workspace";
  const read = useCallback(
    (params) => (scoped ? activityApi.workspaceList(workspaceId, params) : activityApi.list(params)),
    [scoped, workspaceId]
  );

  // Typing narrows the list, but not on every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setFilters((current) => (current.q === term ? current : { ...current, q: term })), 300);
    return () => clearTimeout(timer);
  }, [term]);

  useEffect(() => {
    let live = true;
    (scoped ? activityApi.workspaceOptions(workspaceId) : activityApi.options()).then((res) => {
      if (live && res.success) setOptions(res.content);
    });
    return () => {
      live = false;
    };
  }, [scoped, workspaceId]);

  const query = useMemo(() => {
    const { from, to } = rangeFor(filters.period, filters);
    return {
      q: filters.q,
      category: filters.category,
      workspace: filters.workspace,
      database: filters.database,
      outcome: filters.outcome,
      from,
      to,
    };
  }, [filters]);

  useEffect(() => {
    const mine = latest.current + 1;
    latest.current = mine;
    setLoading(true);
    read(query).then((res) => {
      if (latest.current !== mine) return;
      setLoading(false);
      // The list itself failing is said where the list would be; a failure
      // while adding to it is a toast, so what is already on screen stays.
      if (!res.success) {
        setError(res.msg || "Couldn't load the activity.");
        return;
      }
      setError("");
      setPage(res.content);
      setEvents(res.content.events || []);
      setCursor(res.content.nextCursor || null);
    });
  }, [read, query]);

  const loadMore = async () => {
    setLoadingMore(true);
    const res = await read({ ...query, cursor });
    setLoadingMore(false);
    if (!res.success) {
      setToast({ text: res.msg || "Couldn't load more activity.", tone: "error" });
      return;
    }
    setEvents((current) => [...current, ...(res.content.events || [])]);
    setCursor(res.content.nextCursor || null);
  };

  const set = (key) => (event) => setFilters((current) => ({ ...current, [key]: event.target.value }));
  const filtered = Object.entries(filters).some(([key, value]) => value !== EMPTY[key]);
  const days = useMemo(() => groupByDay(events), [events]);

  const select = (label, key, items, allLabel) => (
    <TextField
      select
      size="small"
      label={label}
      value={filters[key]}
      onChange={set(key)}
      sx={{ minWidth: { sm: 170 }, flex: { xs: "1 1 100%", sm: "0 0 auto" } }}
    >
      <MenuItem value="">{allLabel}</MenuItem>
      {items.map((item) => (
        <MenuItem key={item.value} value={item.value}>
          {item.label}
        </MenuItem>
      ))}
    </TextField>
  );

  return (
    <Box sx={focusRing}>
      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <TextField
          size="small"
          label="Search"
          placeholder="An email address or a key prefix"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ flex: { xs: "1 1 100%", sm: "1 1 260px" }, maxWidth: { sm: 320 } }}
        />
        {select("What happened", "category", (options.categories || []).map((c) => ({ value: c.key, label: c.label })), "Everything")}
        {!scoped &&
          select("Workspace", "workspace", (options.workspaces || []).map((w) => ({ value: w.id, label: w.name })), "Every workspace")}
        {select("Database", "database", (options.databases || []).map((d) => ({ value: d.name, label: d.label })), "Every database")}
        {select("Outcome", "outcome", (options.outcomes || []).map((o) => ({ value: o.key, label: o.label })), "Done and refused")}
        <TextField
          select
          size="small"
          label="When"
          value={filters.period}
          onChange={set("period")}
          sx={{ minWidth: { sm: 160 }, flex: { xs: "1 1 100%", sm: "0 0 auto" } }}
        >
          {PERIODS.map((period) => (
            <MenuItem key={period.key} value={period.key}>
              {period.label}
            </MenuItem>
          ))}
        </TextField>
        {filters.period === "custom" && (
          <>
            <DateField label="From" value={filters.from} onChange={(value) => setFilters((c) => ({ ...c, from: value }))} sx={{ minWidth: 170 }} />
            <DateField label="To" value={filters.to} onChange={(value) => setFilters((c) => ({ ...c, to: value }))} sx={{ minWidth: 170 }} />
          </>
        )}
        {filtered && (
          <Button
            onClick={() => {
              setTerm("");
              setFilters(EMPTY);
            }}
          >
            Clear filters
          </Button>
        )}
      </Stack>

      {error && (
        <Refusal sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Refusal>
      )}

      {loading ? (
        <ListSkeleton rows={6} height={56} />
      ) : events.length === 0 && error ? null : events.length === 0 ? (
        <Surface>
          <EmptyState icon={<HistoryRoundedIcon />} title="Nothing here yet">
            {filtered
              ? "No activity matches these filters. Widen the dates, or clear the filters to see everything."
              : "Signing in, inviting someone, changing a role, creating a key - it all shows here as it happens."}
          </EmptyState>
        </Surface>
      ) : (
        <Stack spacing={2}>
          {days.map((group) => (
            <Box key={group.day}>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75, fontWeight: 700, letterSpacing: "0.02em" }}>
                {dayHeading(group.day)}
              </Typography>
              <Surface>
                {group.events.map((event, index) => (
                  <EventRow key={event.id} event={event} first={index === 0} onOpen={setChosen} />
                ))}
              </Surface>
            </Box>
          ))}
        </Stack>
      )}

      {!loading && events.length > 0 && (
        <Stack alignItems="center" spacing={1} sx={{ mt: 2 }}>
          {cursor ? (
            <LoadingButton variant="outlined" loading={loadingMore} onClick={loadMore}>
              Load more
            </LoadingButton>
          ) : (
            <Typography variant="body2" color="text.secondary">
              That's everything{page?.windowDays ? ` from the last ${page.windowDays} days` : ""}.
              {page?.windowDays ? " Choose a date range to look further back." : ""}
            </Typography>
          )}
        </Stack>
      )}

      <DetailsDrawer event={chosen} onClose={() => setChosen(null)} />
      <Toast notice={toast} onClose={() => setToast("")} />
    </Box>
  );
}
